import type { FieldDescriptor } from './FormScanner.js';
import { CONFIG } from './constants/config.ts';

// Интерфейс для multi-format значений дат/времени
export interface MultiFormatValue {
  formats: {
    iso?: string;          // ISO format: "2025-07-13T19:00:00"
    display?: string;      // Display format: "July 13 at 7:00 PM"
    numeric?: string;      // Numeric format: "07/13/2025 19:00"
    timestamp?: number;    // Unix timestamp: 1731524400000
  };
  originalText: string;    // Оригинальный текст пользователя
}

export interface LLMResponse {
  reply: string;
  autofill?: { [key: string]: string | number | boolean | string[] | MultiFormatValue | undefined };
  originalText?: { [key: string]: string }; // Оригинальный текст пользователя
  confidence?: { [key: string]: number }; // Уровень уверенности для каждого поля (0-1)
  error?: string;
  type?: string; // Тип ошибки от OpenAI
}

export class LLMClient {
  async sendPrompt(prompt: string, fields: FieldDescriptor[], formLanguage?: string): Promise<LLMResponse> {
    console.log('[LLMClient] Making FORM FILLING request to Cloud Function, fields count:', fields.length, 'formLanguage:', formLanguage);
    
    try {
      // Подготавливаем расширенную информацию о полях для multi-format обработки
      const fieldsWithMetadata = fields.map(field => ({
        name: field.name,
        type: field.type,
        label: field.label,
        placeholder: field.placeholder,
        tagName: field.tagName, // Критически важно для определения формата
        required: field.required,
        options: field.options,
        value: field.value
      }));

      console.log('[LLMClient] Fields with metadata prepared:', fieldsWithMetadata.length, 'fields, tagNames:', fieldsWithMetadata.map(f => f.tagName).filter(Boolean));

      const requestBody: { requestType: string; prompt: string; fields: any[]; language?: string } = {
        requestType: 'FORM_FILLING',
        prompt,
        fields: fieldsWithMetadata
      };
      
      if (formLanguage) {
        requestBody.language = formLanguage;
      }

      const response = await fetch(CONFIG.COMPLETE_FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        let errorBody;
        try {
          errorBody = await response.json();
        } catch {
          // Ошибка парсинга JSON, используем текстовый ответ, если есть
          errorBody = { error: await response.text() || response.statusText };
        }
        console.error('LLMClient: Error sending prompt', {
          status: response.status,
          statusText: response.statusText,
          errorBody: errorBody,
        });
        // Возвращаем ошибку в структуре LLMResponse
        return {
          reply: `Error: ${response.status} ${response.statusText}`,
          error: errorBody?.error || response.statusText,
          type: errorBody?.type
        };
      }
      return await response.json() as LLMResponse;
    } catch (error: unknown) {
      console.error('LLMClient: Fetch failed', error);
      // Возвращаем ошибку в структуре LLMResponse
      return {
        reply: 'Failed to connect to the AI service.',
        error: error instanceof Error ? error.message : 'Network error'
      };
    }
  }
} 